/**
 * Centralized Deal & Product Calculation Utilities
 *
 * This module consolidates all calculation logic for deals and products.
 * Use these functions instead of duplicating calculation logic across components.
 *
 * Consolidates logic from:
 * - api/aws/deals/useDealData.ts (deriveMoney, normalizeProductRow)
 * - api/aws/deals/computeTotalValuesFn.ts (computeTotalsBase, computeTotalsFn)
 * - utility/constants.ts (calculateProductStatus, num, round2n, etc.)
 * - utility/calculateTotalMonths.ts (month calculations)
 * - context/DealsDetailsProvider/normalizeDeal.ts (normalizeProductRowOnLoad)
 * - components/Deals/productDiff.ts (calculateProductStatus, diffProducts)
 */

import dayjs, { Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { NewDeal, Product, Totals } from 'types'
import { parseInstanceFromSK, ensureProductSK, productKey } from 'utils/ids'
import { v4 as uuidv4 } from 'uuid'

dayjs.extend(isBetween)

// ============================================================================
// NUMERIC UTILITIES
// ============================================================================

/**
 * Safely convert any value to a number
 */
export const toNumber = (value: unknown): number => {
  if (value === '' || value == null) return 0
  if (typeof value === 'string') {
    // Remove currency formatting
    const cleaned = value.replace(/[$,]/g, '')
    const n = parseFloat(cleaned)
    return Number.isFinite(n) ? n : 0
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Round to 2 decimal places (for currency)
 */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * Round to 2 decimal places and return as fixed string
 */
export const toFixed2 = (n: number): string => round2(n).toFixed(2)

// ============================================================================
// DATE UTILITIES
// ============================================================================

/**
 * Convert any date value to ISO string
 */
export const toISO = (value: unknown): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (value && typeof (value as any).toISOString === 'function') {
    return (value as Date | Dayjs).toISOString()
  }
  // Handle dayjs $d objects
  if (value && typeof value === 'object' && '$d' in value) {
    return dayjs((value as any).$d).toISOString()
  }
  return ''
}

/**
 * Calculate months between two dates (rounded up)
 */
export const calculateMonthsBetween = (startDate: string | Date | Dayjs, endDate: string | Date | Dayjs): number => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  if (!start.isValid() || !end.isValid()) {
    return 0
  }

  const totalMonths = end.diff(start, 'month', true)
  return Math.ceil(totalMonths)
}

/**
 * Get months for a date range, with fallback to 1
 */
export const monthsFor = (startISO?: string, endISO?: string): number => {
  if (!startISO || !endISO) return 1
  const months = calculateMonthsBetween(startISO, endISO)
  return months || 1
}

// ============================================================================
// PRODUCT STATUS CALCULATIONS
// ============================================================================

/** Statuses that should not be auto-computed */
export const MANUAL_STATUSES = new Set(['Cancelled', 'Monthly', 'Expired'])

/** Terminal deal statuses — when a deal is in one of these, product statuses are never recalculated */
export const TERMINAL_DEAL_STATUSES = new Set(['Ended', 'Cancelled', 'Replaced'])

/**
 * Calculate product status based on dates
 */
export const calculateProductStatus = (
  startDate?: string | Date,
  endDate?: string | Date,
  referenceDate: Dayjs = dayjs()
): 'Active' | 'Ended' | 'Pending' | '' => {
  if (!startDate || !endDate) return ''

  const start = dayjs(startDate).startOf('day')
  const end = dayjs(endDate).endOf('day')

  if (!start.isValid() || !end.isValid()) return 'Pending'

  if (referenceDate.isBefore(start)) return 'Pending'
  if (referenceDate.isAfter(end)) return 'Ended'
  return 'Active'
}

/**
 * Compute status only if current status is not a manual override
 */
export const computeStatusIfAuto = (
  currentStatus: string | undefined,
  startISO: string,
  endISO: string
): string => {
  if (currentStatus && MANUAL_STATUSES.has(currentStatus)) {
    return currentStatus
  }
  if (startISO && endISO) {
    return calculateProductStatus(startISO, endISO)
  }
  return currentStatus || ''
}

// ============================================================================
// PRODUCT MONEY CALCULATIONS
// ============================================================================

export interface ProductMoneyFields {
  price: number
  margin: number
  qty: number
  payer: 'GRO' | 'Client'
  mediaSpend: number
  grossProfit: number | string
  billedPrice: number
  total: number
  netTotal: number
}

/**
 * Calculate all money-related fields for a product row
 *
 * Formula:
 * - mediaSpend = price - (price * margin / 100)
 * - grossProfit = price - mediaSpend (i.e., price * margin / 100)
 * - billedPrice = GRO pays full price, Client pays just grossProfit
 * - total = billedPrice * qty
 * - netTotal = price * qty
 */
export const calculateProductMoney = (
  product: Partial<Product>,
  clientMargin: number = 0
): ProductMoneyFields => {
  // Apply default client margin if payer is Client and no margin set
  let margin = toNumber(product.margin)
  if (product.payer === 'Client' && margin === 0 && clientMargin > 0) {
    margin = clientMargin
  }

  const price = toNumber(product.price)
  const qty = toNumber(product.qty) || 1

  // Calculate derived values
  const mediaSpend = price - price * (margin / 100)
  const grossProfitMonthly = price - mediaSpend // = price * margin / 100
  const billedMonthly = product.payer === 'Client' ? grossProfitMonthly : price

  return {
    price,
    margin,
    qty,
    payer: product.payer === 'Client' ? 'Client' : 'GRO',
    mediaSpend: round2(mediaSpend),
    grossProfit: toFixed2(grossProfitMonthly),
    billedPrice: round2(billedMonthly),
    total: round2(billedMonthly * qty),
    netTotal: round2(price * qty),
  }
}

/**
 * Apply money calculations to a product and return updated product
 */
export const deriveProductMoney = (product: Product, clientMargin: number = 0): Product => {
  const money = calculateProductMoney(product, clientMargin)
  return {
    ...product,
    ...money,
  }
}

// ============================================================================
// PRODUCT NORMALIZATION
// ============================================================================

/**
 * Normalize a product row with all required fields and calculations
 */
export const normalizeProduct = (raw: Partial<Product>, clientMargin: number = 0, dealStatus?: string): Product => {
  if (!raw?.PK) throw new Error('Product missing PK')

  const base = { ...raw } as any

  // Derive canonical identity
  const hasSk = typeof base.SK === 'string' && base.SK.startsWith('PRODUCT_')
  const iidFromSk = hasSk ? parseInstanceFromSK(base.SK) : undefined
  const iid = base.instanceId ?? base.rowId ?? iidFromSk ?? uuidv4()
  const SK = hasSk ? String(base.SK) : `PRODUCT_${iid}`

  // Normalize dates to ISO strings
  const startISO = toISO(base.startDate ?? (Array.isArray(base.dateRange) ? base.dateRange[0] : ''))
  const endISO = toISO(base.endDate ?? (Array.isArray(base.dateRange) ? base.dateRange[1] : ''))
  const dateRangeISO: [string, string] = [startISO, endISO]

  // Calculate qty from dates
  const qty = toNumber(base.qty) || monthsFor(startISO, endISO)

  // Calculate status — skip recalculation when parent deal is in a terminal state
  const status = dealStatus && TERMINAL_DEAL_STATUSES.has(dealStatus)
    ? (base.status || 'Ended')
    : computeStatusIfAuto(base.status, startISO, endISO)

  // Normalize sort order
  const sortOrder =
    typeof base.sortOrder === 'number'
      ? base.sortOrder
      : Number.isFinite(+base.sortOrder)
      ? +base.sortOrder
      : undefined

  // Build normalized product
  const normalized: Product = {
    ...base,
    SK,
    instanceId: iid,
    rowId: iid,
    productName: base.productName ?? base.selectedProduct?.title ?? '',
    payer: base.payer === 'Client' ? 'Client' : 'GRO',
    accountUsed: base.accountUsed ?? 'GRO',
    startDate: startISO,
    endDate: endISO,
    dateRange: dateRangeISO,
    qty,
    price: toNumber(base.price),
    margin: toNumber(base.margin),
    sortOrder,
    status,
    createdAt: base.createdAt ?? new Date().toISOString(),
  } as Product

  // Apply money calculations
  return deriveProductMoney(normalized, clientMargin)
}

/**
 * Update a product field and recalculate derived values as needed
 */
export const updateProductField = (
  product: Product,
  field: keyof Product,
  value: unknown,
  extras: Partial<Product> = {},
  clientMargin: number = 0
): Product => {
  const updated: Product = { ...product, ...extras }

  // Set the field value
  ;(updated as any)[field] = value

  // Handle payer coercion (sometimes passed as 0/1)
  if (field === 'payer') {
    if (value === 1 || value === '1') updated.payer = 'Client'
    else if (value === 0 || value === '0') updated.payer = 'GRO'
    else updated.payer = value === 'Client' ? 'Client' : 'GRO'
  }

  // Handle date changes
  if (field === 'dateRange' && Array.isArray(value)) {
    const [s, e] = value
    updated.startDate = toISO(s)
    updated.endDate = toISO(e)
    updated.dateRange = [updated.startDate, updated.endDate] as any
  } else if (field === 'startDate') {
    updated.startDate = toISO(value)
    updated.dateRange = [updated.startDate, updated.endDate || ''] as any
  } else if (field === 'endDate') {
    updated.endDate = toISO(value)
    updated.dateRange = [updated.startDate || '', updated.endDate] as any
  }

  // Recompute qty and status if dates changed
  if (field === 'dateRange' || field === 'startDate' || field === 'endDate') {
    updated.qty = monthsFor(updated.startDate, updated.endDate)
    updated.status = computeStatusIfAuto(updated.status, updated.startDate!, updated.endDate!)
  }

  // Decide if we should recalculate money fields
  const moneyFields: (keyof Product)[] = ['price', 'margin', 'payer', 'qty']
  const shouldRecalc =
    moneyFields.includes(field) ||
    field === 'dateRange' ||
    field === 'startDate' ||
    field === 'endDate' ||
    moneyFields.some(k => extras[k] !== undefined)

  return shouldRecalc ? deriveProductMoney(updated, clientMargin) : updated
}

// ============================================================================
// DEAL TOTALS CALCULATIONS
// ============================================================================

/**
 * Calculate totals for a deal based on its products
 */
export const calculateDealTotals = (deal: NewDeal | undefined, products: Product[]): Totals => {
  const sums = (products ?? []).reduce(
    (acc, row) => {
      // Prefer the product's actual date range; only fall back to `qty` when
      // dates are missing. On flighted deals `qty` stays 1 while the dates
      // carry the real span, so a qty-first read collapsed every product to a
      // single month and undercounted the lifetime total. Mirrors the backend
      // (normalize.ts) and the payment schedule, which both count by dates.
      const months = monthsFor(row.startDate, row.endDate) || toNumber(row.qty) || 1
      const price = toNumber(row.price)
      const mediaSpend = toNumber(row.mediaSpend)
      const gpMonthly = toNumber(row.grossProfit) || (price - mediaSpend)
      const billedMonthly = row.payer === 'Client' ? toNumber(row.billedPrice) || gpMonthly : price

      acc.monthlyTotal += billedMonthly
      acc.spend += mediaSpend
      acc.grossProfitMonthly += gpMonthly
      acc.total += billedMonthly * months
      acc.totalGrossProfit += gpMonthly * months

      return acc
    },
    { monthlyTotal: 0, spend: 0, grossProfitMonthly: 0, total: 0, totalGrossProfit: 0 }
  )

  const implementationFee = toNumber(deal?.implementationFee)

  return {
    grossProfit: round2(sums.grossProfitMonthly + implementationFee),
    monthlyTotal: round2(sums.monthlyTotal),
    spend: round2(sums.spend),
    implementationFee: round2(implementationFee),
    total: round2(sums.total + implementationFee),
    totalGrossProfit: round2(sums.totalGrossProfit + implementationFee),
  }
}

/**
 * Calculate totals including payment amount for CSA/RCSA deals
 */
export const calculateDealTotalsWithPayments = (
  deal: NewDeal | undefined,
  products: Product[]
): Totals & { paymentAmount: number } => {
  const totals = calculateDealTotals(deal, products)

  const dealType = String(deal?.type ?? '').toUpperCase()
  const isCsa = dealType === 'CSA' || dealType === 'RCSA'

  const numberOfPayments = isCsa ? Math.max(1, toNumber(deal?.numberOfPayments)) : 1
  const paymentAmount = numberOfPayments > 0 ? round2(totals.total / numberOfPayments) : totals.total

  return { ...totals, paymentAmount }
}

/**
 * Calculate appropriate number of payments for CSA deals
 * Rule: If total < $2501, use 1 payment; otherwise use deal's numberOfPayments or default to 2
 */
export const calculateNumberOfPayments = (deal: NewDeal, totals?: Totals): number => {
  if (!['CSA', 'RCSA'].includes(deal?.type)) return 1

  const total = totals?.total ?? 0
  if (total < 2501) return 1

  return deal?.numberOfPayments ?? 2
}

// ============================================================================
// DEAL NORMALIZATION
// ============================================================================

const DRAFT_STATUSES = [
  'Draft',
  'Saved',
  'Created',
  'Requested',
  'Rejected',
  'Resolved',
  'Approved',
  'Approval Requested',
  'Updated',
  'Modified',
  'Needs Approval',
  'Completed Internally',
]

/**
 * Normalize a deal with default values
 */
export const normalizeDeal = (deal: NewDeal): NewDeal => {
  const totals = deal.totals
  const numberOfPayments = calculateNumberOfPayments(deal, totals)

  return {
    ...deal,
    recipients: {
      authorizedRecipient: deal?.recipients?.authorizedRecipient || [],
      contactRecipient: deal?.recipients?.contactRecipient || [],
    },
    organizations: deal?.organizations || [],
    contacts: deal?.contacts || [],
    owner: deal?.owner || [],
    createdBy: deal?.createdBy || ([] as any),
    primaryContact: deal?.primaryContact || [],
    isDraft: DRAFT_STATUSES.includes(deal?.status),
    numberOfPayments,
  }
}

// ============================================================================
// DEAL STATUS CALCULATIONS
// ============================================================================

/**
 * Determine deal status based on product statuses
 */
export const calculateDealStatus = (
  currentStatus: string | undefined,
  products: Product[]
): string | undefined => {
  if (!products?.length) return 'Draft'

  const hasActive = products.some(p => p.status === 'Active')
  const hasPending = products.some(p => p.status === 'Pending')

  // Don't override these statuses
  const protectedStatuses = ['Cancelled', 'Replaced']
  if (currentStatus && protectedStatuses.includes(currentStatus)) {
    return currentStatus
  }

  if (hasActive) return 'Active'
  if (hasPending) return 'Pending'
  return 'Ended'
}

// ============================================================================
// PRODUCT DIFF UTILITIES
// ============================================================================

/** Fields to compare when checking if products are equal */
export const PRODUCT_COMPARE_KEYS: (keyof Product)[] = [
  'instanceId',
  'productName',
  'price',
  'billedPrice',
  'margin',
  'mediaSpend',
  'status',
  'startDate',
  'endDate',
  'payer',
  'accountUsed',
  'qty',
  'sortOrder',
  'description',
]

const coerceForCompare = (v: unknown): string | number =>
  v instanceof Date ? v.toISOString() : v == null ? '' : typeof v === 'number' ? v : String(v)

/**
 * Check if two products are equal by comparing key fields
 */
export const areProductsEqual = (a: Product, b: Product, keys = PRODUCT_COMPARE_KEYS): boolean =>
  keys.every(k => coerceForCompare(a[k]) === coerceForCompare(b[k]))

export interface ProductDiff {
  created: Product[]
  updated: Product[]
  deletedIds: string[]
}

/**
 * Diff two product arrays to find created, updated, and deleted products
 */
export const diffProducts = (baseline: Product[], current: Product[]): ProductDiff => {
  const baselineByKey = new Map<string, Product>()
  const currentByKey = new Map<string, Product>()

  for (const p of baseline) baselineByKey.set(productKey(p), p)
  for (const p of current) currentByKey.set(productKey(p), p)

  const created: Product[] = []
  const updated: Product[] = []
  const deletedIds: string[] = []

  // Find created and updated
  for (const [key, now] of currentByKey) {
    const was = baselineByKey.get(key)
    if (!was) {
      created.push(now)
    } else if (!areProductsEqual(was, now)) {
      updated.push(now)
    }
  }

  // Find deleted
  for (const [key, was] of baselineByKey) {
    if (!currentByKey.has(key)) {
      deletedIds.push(was.SK)
    }
  }

  return { created, updated, deletedIds }
}

// ============================================================================
// VALIDATION
// ============================================================================

export interface ValidationResult {
  ok: boolean
  errors: string[]
}

/**
 * Validate a deal before saving/creating contract
 */
export const validateDeal = (deal: NewDeal | undefined, products?: Product[]): ValidationResult => {
  if (!deal) return { ok: false, errors: ['missing deal'] }

  const errors: string[] = []
  const recipients = deal.recipients ?? { authorizedRecipient: [], contactRecipient: [] }
  // No-signature deals skip the signing flow entirely — there's no
  // Authorized Personnel / Client to route the PandaDoc to, so the
  // recipient arrays are intentionally empty. Requiring them here would
  // gate contract creation on a field that's not actually used by the
  // template. Other validation (dates, products) still applies.
  const isNoSignature = !!deal.noSignature

  if (!deal.targetLaunchDate) errors.push('targetLaunchDate')
  if (!deal.dependenciesDueDate) errors.push('dependenciesDueDate')
  if (!isNoSignature && !(recipients.authorizedRecipient?.length > 0)) errors.push('authorizedRecipient')
  if (!isNoSignature && !(recipients.contactRecipient?.length > 0)) errors.push('contactRecipient')
  if (!(products?.length > 0) || products.some(p => !p.productName)) errors.push('products')
  if (!deal.startDate) errors.push('startDate')
  if (!deal.endDate) errors.push('endDate')

  return { ok: errors.length === 0, errors }
}

// ============================================================================
// PRODUCT HELPERS
// ============================================================================

/**
 * Create a new empty product row for a deal
 */
export const createEmptyProduct = (deal: NewDeal): Product => {
  const uid = uuidv4()
  const qty = monthsFor(deal.startDate, deal.endDate)

  return normalizeProduct({
    PK: deal.PK,
    SK: `PRODUCT_${uid}`,
    deal_uuid: deal.deal_uuid,
    instanceId: uid,
    rowId: uid,
    sortOrder: 0,
    productName: '',
    qty,
    price: 0,
    margin: 0,
    payer: 'GRO',
    accountUsed: 'GRO',
    startDate: deal.startDate,
    endDate: deal.endDate,
    dateRange: [deal.startDate, deal.endDate] as any,
    createdAt: new Date().toISOString(),
    EntityType: 'product',
    status: '',
  })
}

/**
 * Sanitize product for database sync
 */
export const sanitizeProductForSync = (product: Product): Product => {
  if (!product.PK) throw new Error('Product missing PK')

  const iid = product.instanceId ?? parseInstanceFromSK(product.SK)
  const SK = ensureProductSK({ ...product, instanceId: iid })

  const [s, e] = Array.isArray(product.dateRange)
    ? product.dateRange
    : [product.startDate, product.endDate]

  return {
    ...product,
    SK,
    instanceId: iid,
    rowId: iid,
    startDate: toISO(s),
    endDate: toISO(e),
    dateRange: [toISO(s), toISO(e)] as any,
    createdAt: product.createdAt,
    price: toNumber(product.price),
    margin: toNumber(product.margin),
    mediaSpend: toNumber(product.mediaSpend),
    billedPrice: toNumber(product.billedPrice),
    qty: toNumber(product.qty),
  }
}

/**
 * Deduplicate and normalize a list of products
 */
export const dedupeAndNormalizeProducts = (products: Partial<Product>[]): Product[] => {
  const map = new Map<string, Product>()

  for (const raw of products ?? []) {
    const normalized = normalizeProduct(raw)
    map.set(`${normalized.PK}|${normalized.SK}`, normalized)
  }

  return [...map.values()]
}

// ============================================================================
// AGGREGATE PRODUCT CALCULATIONS
// ============================================================================

/**
 * Calculate total months and date range across all products
 */
export const calculateProductsDateRange = (products: Product[]) => {
  if (!Array.isArray(products) || products.length === 0) {
    return { totalMonths: 0, dateRange: { start: null, end: null }, allSame: true }
  }

  const validRanges = products
    .map(p => {
      const start = dayjs(p.startDate)
      const end = dayjs(p.endDate)
      if (!start.isValid() || !end.isValid()) return null
      return { start, end }
    })
    .filter(Boolean) as { start: Dayjs; end: Dayjs }[]

  if (validRanges.length === 0) {
    return { totalMonths: 0, dateRange: { start: null, end: null }, allSame: true }
  }

  const earliestStart = validRanges.reduce((min, r) => (r.start.isBefore(min) ? r.start : min), validRanges[0].start)
  const latestEnd = validRanges.reduce((max, r) => (r.end.isAfter(max) ? r.end : max), validRanges[0].end)

  const totalMonths = Math.ceil(latestEnd.diff(earliestStart, 'month', true))

  const allSame = validRanges.every(r => {
    const duration = Math.ceil(r.end.diff(r.start, 'month', true))
    return duration === totalMonths
  })

  return {
    totalMonths,
    dateRange: {
      start: earliestStart.format('MM/DD/YYYY'),
      end: latestEnd.format('MM/DD/YYYY'),
    },
    allSame,
  }
}
